import React from "react";
import { useOrganization, useSession } from "@clerk/react";

import { createSupabaseBrowserClient, type AppSupabaseClient } from "@/lib/supabase/client";

interface CachedClient {
  client: AppSupabaseClient;
  sessionId: string;
}


function decodeJwtPayload(token: string): null | Record<string, unknown> {
  const [, payload] = token.split(".");

  if (!payload) {
    return null;
  }

  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const decoded = globalThis.atob(padded);
    const bytes = Uint8Array.from(decoded, (character) => character.codePointAt(0) ?? 0);
    const value: unknown = JSON.parse(new TextDecoder().decode(bytes));

    return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function validateSupabaseRoleClaim(token: string): void {
  const role = decodeJwtPayload(token)?.role;

  if (role !== "authenticated") {
    throw new Error(
      "Clerk session token is missing the `role: authenticated` claim. Add it under Clerk's Sessions → customize session token, then sign out and back in."
    );
  }
}

export function useSupabaseClient(): () => Promise<AppSupabaseClient> {
  const { organization } = useOrganization();
  const { session } = useSession();
  const cachedClient = React.useRef<CachedClient | null>(null);
  const organizationId = organization?.id ?? null;

  return React.useCallback(async (): Promise<AppSupabaseClient> => {
    if (!session) {
      throw new Error("Missing Clerk session for Supabase access.");
    }

    const cacheKey = `${session.id}:${organizationId ?? "personal"}`;

    if (cachedClient.current?.sessionId === cacheKey) {
      return cachedClient.current.client;
    }

    const client = createSupabaseBrowserClient(async () => {
      const token = await session
        .getToken()
        .catch(() => null);

      if (!token) {
        throw new Error("Missing Clerk session token for Supabase access.");
      }

      validateSupabaseRoleClaim(token);

      return token;
    });

    cachedClient.current = { client, sessionId: cacheKey };
    return client;
  }, [organizationId, session]);
}
